import * as _ from 'lodash';
import { Cartesian2, Cartesian3, Viewer } from 'cesium';

export interface Scale {
    text: string;
    width: number;
}

export class ScaleIndicator {
    _viewer: Viewer | null;
    _listeners;
    _model: Scale;
    _metreIntervals;
    _minimumWidthInPixels;

    constructor(metreIntervals, minimumWidth) {
        this._viewer = null;
        this._listeners = [];
        this._model = {
            text: '',
            width: 0,
        };

        if (!metreIntervals) {
            throw new Error('No metre intervals provided to ScaleIndicator');
        }
        this._metreIntervals = metreIntervals;

        if (_.isNull(minimumWidth)) {
            throw new Error('No minimum width provided to ScaleIndicator');
        }
        this._minimumWidthInPixels = minimumWidth;
    }

    initialise(viewer: Viewer) {
        this._viewer = viewer;

        this._registerToCameraChanges();

        this._buildScaleIndicatorModel();
    }

    _registerToCameraChanges() {
        this._viewer?.camera.changed.addEventListener(() => {
            this._buildScaleIndicatorModel();
        });
        this._viewer?.camera.moveStart.addEventListener(() => {
            this._buildScaleIndicatorModel();
        });
        this._viewer?.camera.moveEnd.addEventListener(() => {
            this._buildScaleIndicatorModel();
        });
    }

    onChange(listener) {
        this._listeners.push(listener);
        return () => {
            _.remove(this._listeners, (l) => {
                return l === listener;
            });
        };
    }

    _runListeners() {
        _.forEach(this._listeners, (listener) => {
            listener(this._model);
        });
    }

    _getScaleDistanceInMetres() {
        const camera = this._viewer?.camera;
        const scene = this._viewer?.scene;
        if (!scene || !camera) return null;

        // Determine the center of the viewport
        const rightScalePosition = new Cartesian2();
        rightScalePosition.x = scene.canvas.clientWidth / 2 + this._minimumWidthInPixels / 2;
        rightScalePosition.y = scene.canvas.clientHeight / 2;

        // Determine the cartesian3 to rotate around
        // getPickRay can now return undefined in Cesium 1.91
        const rightRay = camera.getPickRay(rightScalePosition);
        let rightCart3;
        if (rightRay) {
            rightCart3 = scene.globe.pick(rightRay, scene);
        }

        // Determine the center of the viewport
        const leftScalePosition = new Cartesian2();
        leftScalePosition.x = scene.canvas.clientWidth / 2 - this._minimumWidthInPixels / 2;
        leftScalePosition.y = scene.canvas.clientHeight / 2;

        // Determine the cartesian3 to rotate around
        // getPickRay can now return undefined in Cesium 1.91
        const leftRay = camera.getPickRay(leftScalePosition);
        let leftCart3;
        if (leftRay) {
            leftCart3 = scene.globe.pick(leftRay, scene);
        }

        if (rightCart3 && leftCart3) {
            return Cartesian3.distance(rightCart3, leftCart3);
        } else {
            return null;
        }
    }

    _buildScaleIndicatorModel() {
        let model = null;

        let distance: number = null;
        try {
            distance = this._getScaleDistanceInMetres();
        } catch {
            console.error('error calculating scale distance');
        }

        if (distance) {
            const intervals = this._metreIntervals;

            for (let i = 0; i < intervals.length; ++i) {
                if (distance < intervals[i]) {
                    model = {};
                    const marker = intervals[i];
                    if (marker >= 1000) {
                        model.text = `${marker / 1000} km`;
                    } else {
                        model.text = `${marker} m`;
                    }
                    model.width = Math.floor(
                        (this._minimumWidthInPixels * intervals[i]) / distance,
                    );
                    break;
                }
            }
        }

        if (model) {
            this._model = model;
        } else {
            this._model = {
                text: '',
                width: 0,
            };
        }

        this._runListeners();
    }
}
